import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { WaveBadge } from "@/pages/admin/Dashboard";
import { toast } from "sonner";
import { Activity, Clock, CheckCircle2, Loader, HeartPulse, Wrench, ArrowRight } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const FLAG_META = {
  incomplete: { label: "Tidak lengkap", bg: "#FEF3C7", fg: "#B45309" },
  straightlining: { label: "Straightlining", bg: "#FEE4E2", fg: "#B42318" },
  high_unsure: { label: "Banyak 'Tidak yakin'", bg: "#DBEAFE", fg: "#1D4ED8" },
  speeding: { label: "Terlalu cepat", bg: "#FEE4E2", fg: "#B42318" },
};
const STATUS_LABEL = { invited: "Diundang", opened: "Dibuka", in_progress: "Berjalan", submitted: "Terkirim", expired: "Kedaluwarsa", revoked: "Dicabut" };

const HEALTH_FILTERS = [
  { key: "semua", label: "Semua" },
  { key: "siap", label: "Siap" },
  { key: "perlu_perhatian", label: "Perlu perhatian" },
  { key: "live_bermasalah", label: "Live bermasalah" },
];
const OK_GREEN = "#166534";
const BAD_RED = "#B91C1C";

function fmtDur(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

function HealthItem({ label, testid, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: COLORS.textSoft }}>{label}</div>
      <div className="mt-0.5 text-xs font-semibold" data-testid={testid}>{children}</div>
    </div>
  );
}

function WaveHealthCard({ h, canRepair, onRepair }) {
  const scoringNode =
    h.scoring_snapshot === "tidak_diperlukan" ? (
      <span style={{ color: COLORS.textSoft }}>Tidak diperlukan</span>
    ) : (
      <span style={{ color: h.scoring_snapshot === "siap" ? OK_GREEN : BAD_RED }}>
        {h.scoring_snapshot === "siap" ? "Siap" : "Belum siap"}
      </span>
    );
  return (
    <div className="rounded-comiq border bg-white p-4" style={{ borderColor: COLORS.line }} data-testid={`wave-health-card-${h.wave_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <span className="text-sm font-semibold" style={{ color: COLORS.ink }}>{h.label}</span>
          {h.is_demo && <span className="ml-2 text-[10px] font-bold uppercase" style={{ color: "#B45309" }}>demo</span>}
          <span className="block truncate text-xs" style={{ color: COLORS.textSoft }} data-testid={`health-${h.wave_id}-study`}>
            {h.study_title || "—"}
          </span>
        </div>
        <WaveBadge status={h.status} />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <HealthItem label="Snapshot instrumen" testid={`health-${h.wave_id}-instrument`}>
          <span style={{ color: h.instrument_snapshot === "siap" ? OK_GREEN : BAD_RED }}>
            {h.instrument_snapshot === "siap" ? "Siap" : "Belum siap"}
          </span>
        </HealthItem>
        <HealthItem label="Versi instrumen" testid={`health-${h.wave_id}-version`}>
          <span style={{ color: COLORS.ink }}>
            {h.instrument_version_no ? `v${h.instrument_version_no} · ${h.instrument_version_status}` : "—"}
          </span>
        </HealthItem>
        <HealthItem label="Snapshot scoring" testid={`health-${h.wave_id}-scoring`}>{scoringNode}</HealthItem>
        <HealthItem label="Consent aktif" testid={`health-${h.wave_id}-consent`}>
          <span style={{ color: h.consent_active ? OK_GREEN : BAD_RED }}>{h.consent_active ? "Ya" : "Tidak"}</span>
        </HealthItem>
        <HealthItem label="Kesiapan Go Live" testid={`health-${h.wave_id}-readiness`}>
          <span style={{ color: h.ready_for_live ? OK_GREEN : BAD_RED }}>{h.ready_for_live ? "Siap" : "Tidak siap"}</span>
        </HealthItem>
      </div>

      {h.reasons.length > 0 && (
        <ul className="mt-3 space-y-0.5 rounded-comiq-sm px-3 py-2 text-xs" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid={`health-${h.wave_id}-reasons`}>
          {h.reasons.map((r) => <li key={r}>• {r}</li>)}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <Link
          to={`/admin/waves?study=${h.study_id}`}
          data-testid={`health-${h.wave_id}-detail-btn`}
          className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors"
          style={{ borderColor: COLORS.lineStrong, color: COLORS.ink }}
        >
          Lihat detail <ArrowRight size={12} />
        </Link>
        {canRepair && h.repair_available && (
          <button
            onClick={() => onRepair(h.wave_id)}
            data-testid={`health-${h.wave_id}-repair-btn`}
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors"
            style={{ borderColor: "#B45309", color: "#B45309" }}
          >
            <Wrench size={12} /> Perbaiki koneksi instrumen
          </button>
        )}
      </div>
    </div>
  );
}

export default function Monitoring() {
  const { user } = useAuth();
  const [waves, setWaves] = useState([]);
  const [waveId, setWaveId] = useState("");
  const [data, setData] = useState(null);
  const [health, setHealth] = useState([]);
  const [filter, setFilter] = useState("semua");

  const loadHealth = () => api.get("/admin/waves-health").then((r) => setHealth(r.data)).catch(() => {});

  useEffect(() => {
    loadHealth();
    api.get("/admin/waves").then((r) => {
      setWaves(r.data);
      const pick = r.data.find((w) => w.is_demo && w.status !== "archived") || r.data.find((w) => w.status !== "archived");
      if (pick) setWaveId(pick.wave_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!waveId) return;
    api.get(`/admin/waves/${waveId}/monitoring`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [waveId]);

  const repair = async (waveId) => {
    try {
      await api.post(`/admin/waves/${waveId}/pin-instrument`);
      toast.success("Koneksi instrumen diperbaiki — snapshot tersimpan");
      loadHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbaiki koneksi instrumen");
    }
  };

  const filteredHealth = filter === "semua" ? health : health.filter((h) => h.category === filter);
  const counts = Object.fromEntries(HEALTH_FILTERS.map((f) => [f.key, f.key === "semua" ? health.length : health.filter((h) => h.category === f.key).length]));

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const t = data?.totals;

  return (
    <div className="p-6">
      <Eyebrow>Pemantauan Lapangan</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Monitoring gelombang</h1>

      <div className="mt-6 rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }} data-testid="wave-health-section">
        <div className="flex items-center gap-2">
          <HeartPulse size={16} style={{ color: COLORS.orangeInk }} />
          <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Kesehatan Gelombang</h2>
        </div>
        <p className="mt-1 text-xs" style={{ color: COLORS.textSoft }}>
          Kesiapan Go Live memakai aturan validasi yang sama dengan backend. Halaman ini hanya membaca data — tidak mengubah status, snapshot, atau respons apa pun.
        </p>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="wave-health-filters">
          {HEALTH_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`wave-health-filter-${f.key}`}
              className="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
              style={{
                borderColor: filter === f.key ? COLORS.orange : COLORS.line,
                background: filter === f.key ? "rgba(242,84,27,0.08)" : "#fff",
                color: filter === f.key ? COLORS.orangeInk : COLORS.ink,
              }}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-3">
          {filteredHealth.length === 0 && (
            <p className="py-4 text-center text-xs" style={{ color: COLORS.textSoft }} data-testid="wave-health-empty">
              Tidak ada gelombang pada filter ini.
            </p>
          )}
          {filteredHealth.map((h) => (
            <WaveHealthCard key={h.wave_id} h={h} canRepair={user?.role !== "analyst"} onRepair={repair} />
          ))}
        </div>
      </div>

      <div className="mt-6">
        <select value={waveId} onChange={(e) => setWaveId(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="monitoring-wave-select">
          {waves.map((w) => <option key={w.wave_id} value={w.wave_id}>{w.label}</option>)}
        </select>
      </div>

      {data && (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="monitoring-kpis">
            {[
              { icon: Activity, label: "Diundang", value: t.invitations },
              { icon: Loader, label: "Berjalan", value: t.in_progress },
              { icon: CheckCircle2, label: "Terkirim", value: t.submitted },
              { icon: Clock, label: "Rata-rata durasi", value: fmtDur(t.avg_duration_sec) },
            ].map((k) => (
              <div key={k.label} className="rounded-comiq border bg-comiq-surface-alt p-4" style={{ borderColor: COLORS.line }}>
                <k.icon size={16} style={{ color: COLORS.orangeInk }} />
                <div className="mt-2 text-2xl font-bold" style={{ color: COLORS.ink }}>{k.value}</div>
                <div className="text-xs" style={{ color: COLORS.textSoft }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-comiq border bg-comiq-surface-alt p-4" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between text-sm">
              <span style={{ color: COLORS.textSoft }}>Tingkat penyelesaian</span>
              <span className="font-bold" style={{ color: COLORS.ink }}>{Math.round(t.completion_rate * 100)}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full" style={{ background: COLORS.line }}>
              <div className="h-full rounded-full" style={{ width: `${t.completion_rate * 100}%`, background: COLORS.orange }} data-testid="completion-bar" />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Corong status</h3>
              <div className="mt-3 space-y-2">
                {Object.entries(data.by_status).map(([s, n]) => (
                  <div key={s} className="flex items-center gap-3 text-sm">
                    <span className="w-28" style={{ color: COLORS.textSoft }}>{STATUS_LABEL[s] || s}</span>
                    <div className="h-4 flex-1 overflow-hidden rounded" style={{ background: COLORS.paper }}>
                      <div className="h-full rounded" style={{ width: `${(n / t.invitations) * 100}%`, background: s === "submitted" ? "#15803D" : COLORS.ink, opacity: s === "submitted" ? 1 : 0.55 }} />
                    </div>
                    <span className="w-8 text-right font-mono text-xs" style={{ color: COLORS.ink }}>{n}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Penanda kualitas data</h3>
              <p className="mt-0.5 text-[0.7rem]" style={{ color: COLORS.textSoft }}>Bersifat advisory — tidak ada respons yang dikecualikan otomatis oleh ambang demo ini.</p>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="flags-summary">
                {Object.entries(data.flags_summary).map(([f, n]) => (
                  <span key={f} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" style={{ background: FLAG_META[f].bg, color: FLAG_META[f].fg }}>
                    {FLAG_META[f].label}: {n}
                  </span>
                ))}
              </div>
              {data.submissions_by_day.length > 0 && (
                <div className="mt-5 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.submissions_by_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.line} vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: COLORS.textSoft }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: COLORS.textSoft }} width={20} />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.orange} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 overflow-hidden rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="monitoring-table">
                <thead>
                  <tr style={{ background: COLORS.surface, color: COLORS.textSoft }}>
                    {["Kode", "Status", "Progres", "Durasi", "Kualitas", "Aktivitas terakhir"].map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm" style={{ color: COLORS.textSoft }}>Belum ada aktivitas responden.</td></tr>}
                  {data.sessions.map((r) => (
                    <tr key={r.participant_code} className="border-t" style={{ borderColor: COLORS.line }} data-testid={`mon-row-${r.participant_code}`}>
                      <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: COLORS.ink }}>{r.participant_code}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.textSoft }}>{STATUS_LABEL[r.status] || r.status}</td>
                      <td className="px-4 py-3" style={{ color: COLORS.ink }}>{r.answered}/{r.total}{r.empty > 0 && r.status === "submitted" ? ` · ${r.empty} kosong` : ""}</td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: COLORS.textSoft }}>{fmtDur(r.duration_sec)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.flags.length === 0 ? <span className="text-xs" style={{ color: "#15803D" }}>OK</span> : r.flags.map((f) => (
                            <span key={f} className="rounded-full px-2 py-0.5 text-[0.65rem] font-semibold" style={{ background: FLAG_META[f].bg, color: FLAG_META[f].fg }} data-testid={`flag-${f}`}>{FLAG_META[f].label}</span>
                          ))}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: COLORS.textSoft }}>{r.last_activity_at ? new Date(r.last_activity_at).toLocaleString("id-ID") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
